import { AgentType, Model, MotionMapperType } from "../models/Model";
import { WorldRenderer } from "../panels/WorldRenderer";
import * as THREE from "three";
import { QuadrupedVisual } from "./QuadrupedVisual";
import { BaseVisual } from "./BaseVisual";
import { Visual } from "../models/Visual";
import { AnimatedGLBVisual } from "./AnimatedGLBVisual";
import { ModelBasedVisual } from "./ModelBasedVisual";
import { WREvents } from "../panels/WREvents";
import { createIntristicVisual, IntristicVisualModel } from "./IntristicVisualModel";
import { LightVisual } from "./LightVisual";
import { FloorVisual } from "./FloorVisual";
import { HumanVisual } from "./HumanVisual";
import { MarkerVisual } from "./MarkerVisual";
import { ArmVisual } from "./ArmVisual";
import { DragonflyVisual } from "./DragonflyVisual";
import { C4Visual } from "./C4Visual";

export async function createNewVisualFromModel(wr: WorldRenderer, model: Model, point: THREE.Vector3) {
  const site = wr.dataService.activeSite;
  if (!site) {
    console.warn("No active site to create visual on.");
    return;
  }
  if (!site._id) {
    console.warn("Active site does not have an ID.");
    return;
  }
  const pointOnSite = site.obj.worldToLocal(point.clone());
  const distance = pointOnSite.length();
  if (distance > site.grid.size * 1.5) {
    console.warn(`Point is too far from site anchor: ${distance} > ${site.grid.size * 0.5}`);
    return;
  }
  // console.log({ pointOnSite });
  let newVisual: BaseVisual | null = null;
  if ((model as any).visualType) {
    newVisual = createIntristicVisual(model as IntristicVisualModel);
  } else if (model.agentType === AgentType.Dragonfly) {
    newVisual = new DragonflyVisual();
  } else if (model.motionMapper.type === MotionMapperType.QuadrupedRobot) {
    newVisual = new QuadrupedVisual();
  } else if (model.agentType === AgentType.RobotArm) {
    newVisual = new ArmVisual();
  } else if (model.agentType === AgentType.Human) {
    newVisual = new HumanVisual();
  } else if (model.motionMapper.type === MotionMapperType.RobotArm) {
    // newVisual = new ArmVisual();
  } else if (model.motionMapper.type === MotionMapperType.AnimatedGLB) {
    newVisual = new AnimatedGLBVisual();
  } else {
    console.warn(`Unsupported model type for visual creation: ${model.motionMapper.type}`);
  }

  if (newVisual) {
    const visual = newVisual;
    visual.wr = wr;
    visual.siteAnchorId = site._id;
    if (visual instanceof ModelBasedVisual) {
      visual.modelId = model._id;
    }
    visual.position = { x: pointOnSite.x, y: pointOnSite.y, z: pointOnSite.z };
    await visual.save();
    const loaded = await wr.dataService.loadVisual(visual._id);
    wr.emitEvent(WREvents.VisualSelected, loaded);
  }
}

const typeMap = new Map<string, BaseVisual>();
export function registerType<T extends BaseVisual>(constructor: new () => T) {
  const vis = new constructor();
  typeMap.set(vis.getType(), vis);
}

export function createVisualFromType(type: string): BaseVisual {
  const proto = typeMap.get(type);
  if (!proto) {
    const keysArray = Array.from(typeMap.keys());
    throw new Error(`Unknown visual type: ${type} ${keysArray.join(',')}`);
  }
  const constructor = proto.constructor as new () => BaseVisual;
  const visual = new constructor();
  return visual;
}

export async function buildVisual(wr: WorldRenderer, visual: Visual): Promise<BaseVisual> {
  const vis = createVisualFromType(visual.type);
  vis.read(visual);
  await vis.load(wr);
  return vis;
}

registerType(QuadrupedVisual);
registerType(AnimatedGLBVisual); // Assuming HumanVisual is defined elsewhere
registerType(LightVisual);
registerType(FloorVisual);
registerType(HumanVisual);
registerType(MarkerVisual);
registerType(ArmVisual);
registerType(DragonflyVisual);
registerType(C4Visual);
